import React, { useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { useGoogleFonts } from "@/hooks/useGoogleFonts";
import { DesignSystem, FooterBlockProperties } from "@/types/ThemeTypes";
import { defaultFooterBlockProps } from "@/config/defaultLandingConfig";
import styles from "./FooterBlock.module.css";
import richtextStyles from "@/components/share/richtext.module.css";
import BaseModal, { ResponsiveWidths } from "@/components/share/BaseModal";
import { useFetchFooterLegal } from "@/hooks/useFetchFooterLegal";
import { poweredByInk } from "@/utils/poweredByInk";
import { REACT_APP_CMS_FE_DOMAIN as CMS_FE_DOMAIN } from "@/config";

export interface FooterBlockProps {
  properties?: FooterBlockProperties;
  designSystem?: DesignSystem;
  tenantDomain?: string;
  apiVersion?: string;
}

// Helper function to format border CSS value
const formatBorder = (width: string, color: string): string => {
  const borderWidth = width || "0px";
  if (borderWidth === "0px" || borderWidth === "0" || !borderWidth) {
    return "none";
  }
  return `${borderWidth} solid ${color || "#000000"}`;
};

const FOOTER_LEGAL_WIDTHS: ResponsiveWidths = {
  MOBILE: 358,
  TABLET: 700,
  DESKTOP: 900,
  LARGE_DESKTOP: 900,
} as const;

// Primary logo path (can be customized)
// const PRIMARY_LOGO = "/static/img/logo/logo-primary.png";

// DOMPurify config for legal HTML
const LEGAL_HTML_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "b",
    "i",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "div",
    "span",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "title", "style"],
  FORBID_TAGS: [
    "script",
    "object",
    "embed",
    "iframe",
    "form",
    "input",
    "button",
    "img",
  ],
  FORBID_ATTR: [
    "onerror",
    "onload",
    "onclick",
    "onmouseover",
    "onfocus",
    "onblur",
  ],
};

const SWIIFT_STUDIO_URL = "https://swiift.studio/";
const SWIIFT_STUDIO_LOGO =
  CMS_FE_DOMAIN + "/static/img/logo/swiift-studio-logo-fa.png";
const SWIIFT_STUDIO_REVERSED_LOGO =
  CMS_FE_DOMAIN + "/static/img/logo/swiift-studio-reversed-logo-fa.png";
// Same-origin copy for the tinted logo: a CSS mask is fetched with CORS, and
// the CMS domain's static files are not served with CORS headers.
const SWIIFT_STUDIO_LOGO_MASK = "/static/img/logo/swiift-studio-logo-fa.png";
// Intrinsic size of the logo file, so the tinted box keeps its proportions.
const SWIIFT_STUDIO_LOGO_ASPECT = "2384 / 623";

const FooterBlock: React.FC<FooterBlockProps> = ({
  properties,
  designSystem,
  tenantDomain,
  apiVersion,
}) => {
  const props = {
    ...defaultFooterBlockProps,
    ...properties,
  };

  useGoogleFonts([props.menuFontFamily || "Poppins"]);

  // The brand can pick the "Powered by" colour; unset stays automatic and
  // must match the CMS preview's pick (see poweredByInk).
  const poweredBy = poweredByInk(props.poweredByColor, props.backgroundColor);

  const { privacy, termAndCondition, cookiePolicy } = useFetchFooterLegal({
    tenantDomain,
    apiVersion,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");

  const openModal = useCallback(
    (node: { title: string; description: string } | null) => {
      if (!node) return;
      setModalTitle(node.title || "");
      setModalContent(node.description || "");
      setIsOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => setIsOpen(false), []);

  const handleLogoClick = () => {
    if (props.logoLink) {
      window.location.href = props.logoLink;
    }
  };

  // Sanitize HTML using DOMPurify directly
  const sanitizeLegalHtml = (dirty: string): string => {
    if (!dirty || typeof dirty !== "string") {
      return "";
    }

    try {
      return DOMPurify.sanitize(dirty, LEGAL_HTML_CONFIG);
    } catch (error) {
      console.warn("HTML sanitization failed:", error);
      // Return plain text if sanitization fails
      return dirty.replace(/<[^>]*>/g, "");
    }
  };

  return (
    <>
      <div
        className={styles.bb_minimalism_footerContainer}
        style={{
          borderTop: formatBorder(props.borderTop, props.borderColor),
          borderRight: formatBorder(props.borderRight, props.borderColor),
          borderBottom: formatBorder(props.borderBottom, props.borderColor),
          borderLeft: formatBorder(props.borderLeft, props.borderColor),
          backgroundColor: props.backgroundColor,
        }}
      >
        <div className={styles.bb_minimalism_footerContent}>
          {/* Logo Section */}
          {props.logoEnabled && (
            <div className={styles.bb_minimalism_logoContainer}>
              {props.logoImage ? (
                <img
                  src={props.logoImage}
                  alt="Logo"
                  className={styles.bb_minimalism_logoImage}
                  onClick={handleLogoClick}
                />
              ) : null}
            </div>
          )}

          {/* Menu Section */}
          <div
            className={styles.bb_minimalism_menuContainer}
            style={{
              backgroundColor: props.backgroundColor,
            }}
          >
            {props.menuEnabled && (
              <>
                <div className={styles.bb_minimalism_legalLinkContainer}>
                  <button
                    className={styles.bb_minimalism_legalLink}
                    style={{
                      color: props.menuTextColor,
                      fontFamily: props.menuFontFamily,
                      fontSize: `${props.menuFontSize}px`,
                      fontWeight: props.menuBold ? "bold" : "normal",
                      fontStyle: props.menuItalic ? "italic" : "normal",
                      textDecoration: props.menuUnderline
                        ? "underline"
                        : "none",
                      textTransform:
                        props.menuFontTransform as React.CSSProperties["textTransform"],
                    }}
                    onClick={() => openModal(termAndCondition)}
                  >
                    {termAndCondition?.title || "TERMS & CONDITIONS"}
                  </button>
                </div>
                <div className={styles.bb_minimalism_legalLinkContainer}>
                  <button
                    className={styles.bb_minimalism_legalLink}
                    style={{
                      color: props.menuTextColor,
                      fontFamily: props.menuFontFamily,
                      fontSize: `${props.menuFontSize}px`,
                      fontWeight: props.menuBold ? "bold" : "normal",
                      fontStyle: props.menuItalic ? "italic" : "normal",
                      textDecoration: props.menuUnderline
                        ? "underline"
                        : "none",
                      textTransform:
                        props.menuFontTransform as React.CSSProperties["textTransform"],
                    }}
                    onClick={() => openModal(privacy)}
                  >
                    {privacy?.title || "PRIVACY POLICY"}
                  </button>
                </div>
                <div className={styles.bb_minimalism_legalLinkContainer}>
                  <button
                    className={styles.bb_minimalism_legalLink}
                    style={{
                      color: props.menuTextColor,
                      fontFamily: props.menuFontFamily,
                      fontSize: `${props.menuFontSize}px`,
                      fontWeight: props.menuBold ? "bold" : "normal",
                      fontStyle: props.menuItalic ? "italic" : "normal",
                      textDecoration: props.menuUnderline
                        ? "underline"
                        : "none",
                      textTransform:
                        props.menuFontTransform as React.CSSProperties["textTransform"],
                    }}
                    onClick={() => openModal(cookiePolicy)}
                  >
                    {cookiePolicy?.title || "COOKIE"}
                  </button>
                </div>
              </>
            )}
            <div
              className={styles.bb_minimalism_poweredByContainer}
              style={{
                backgroundColor: props.backgroundColor,
              }}
            >
              <span
                className={styles.bb_minimalism_poweredByText}
                style={{ color: poweredBy.color }}
              >
                Powered by
                <a
                  href={SWIIFT_STUDIO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {poweredBy.custom ? (
                    <span
                      role="img"
                      aria-label="SWiiFT STUDIO"
                      style={{
                        height: "2em",
                        display: "block",
                        aspectRatio: SWIIFT_STUDIO_LOGO_ASPECT,
                        backgroundColor: poweredBy.color,
                        WebkitMask: `url(${SWIIFT_STUDIO_LOGO_MASK}) center / contain no-repeat`,
                        mask: `url(${SWIIFT_STUDIO_LOGO_MASK}) center / contain no-repeat`,
                      }}
                    />
                  ) : (
                    <img
                      src={
                        poweredBy.color === "#FFFFFF"
                          ? SWIIFT_STUDIO_REVERSED_LOGO
                          : SWIIFT_STUDIO_LOGO
                      }
                      alt="SWiiFT STUDIO"
                      style={{
                        height: "2em",
                        display: "block",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>

      <BaseModal
        open={isOpen}
        handleClose={closeModal}
        responsiveWidths={FOOTER_LEGAL_WIDTHS}
        maxHeight="80vh"
        borderRadius={4}
        title={modalTitle}
        fontFamily={designSystem?.secondaryFont || "Poppins"}
        designSystem={designSystem}
      >
        <div
          className={`${styles.bb_legal_modal_content} ${richtextStyles.bb_minimalism_richtext}`}
          dangerouslySetInnerHTML={{
            __html: sanitizeLegalHtml(modalContent || ""),
          }}
          style={{
            fontFamily: designSystem?.secondaryFont || "Poppins",
            color: "#000000",
            padding: "16px",
          }}
        />
      </BaseModal>
    </>
  );
};

export default FooterBlock;
